import { readCorrectedSubjects } from "@/lib/live/replay/replay-subjects";
import type { VideoSubject } from "@/lib/live/video/video-types";

import { ApiError } from "./api-client";
import { getToken } from "./auth";
import { isLiveCode, type LiveRecitalRecording, type LiveVisibility } from "./live-api";

/**
 * Les récitals, côté API : programmer, s'inscrire, démarrer, annuler.
 *
 * # Un fichier à côté de `live-api.ts`, pas dedans
 *
 * Un récital n'est pas une séance de plus : c'est une représentation
 * **planifiée**, avec un seul interprète et un public qui regarde. Il a son
 * propre préfixe (`/api/live/recitals`), ses propres refus et sa propre
 * forme. Le loger dans `live-api.ts` aurait fait grossir le module des
 * séances d'un domaine qu'il n'a pas à connaître — l'entrée dans la salle,
 * elle, reste un jeton de séance ordinaire (`joinLiveSession`), et c'est ce
 * jeton qui porte le rôle.
 *
 * # La plomberie est recopiée, et c'est voulu
 *
 * `live-api.ts` garde ses `get`/`post` privés, et son `post` ne rend qu'un
 * billet en y joignant l'identité de l'onglet — ce qui n'a aucun sens pour
 * programmer un récital. Le strict nécessaire (en-tête de compte, lecture du
 * message d'erreur) est repris ici plutôt qu'exporté de là-bas : partager
 * trois lignes aurait couplé deux modules qui n'ont en commun que la forme
 * d'un `{ error }`, et ce fichier doit pouvoir changer sans qu'on relise
 * l'autre.
 *
 * Le contrat est celui de `docs/spec/recitals.md`, et de
 * `docs/spec/live-recitals-only.md` pour la visibilité, le code et « mes
 * récitals ».
 */

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

const RECITALS_PATH = "/api/live/recitals";

/**
 * Les enregistrements déposés. Sous `/api/live/` comme le reste du service, et
 * non sous `/api/recordings` : c'est l'enregistreur du live qui les dépose, et
 * l'annonce arrive déjà là.
 */
const RECORDINGS_PATH = "/api/live/recordings";

/** Un identifiant de récital est un UUID (v7 côté API ; la forme suffit ici). */
const RECITAL_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * L'état d'un récital. Dérivé côté API, jamais choisi ici.
 *
 * `scheduled` — annoncé, personne sur scène ; `live` — l'interprète a ouvert
 * la salle ; `ended` — sa séance est fermée ; `cancelled` — retiré avant
 * d'avoir commencé.
 */
export type RecitalStatus = "scheduled" | "live" | "ended" | "cancelled";

/**
 * L'enregistrement d'un récital : rien ne s'enregistre sans que l'interprète
 * l'ait demandé. Voir `LiveRecitalRecording`, dont celui-ci est la part que la
 * base connaît.
 */
export type RecitalRecording = Exclude<LiveRecitalRecording, "unavailable" | "full">;

/** Le morceau annoncé. `null` quand l'interprète n'en a pas dit. */
export interface RecitalPiece {
  id: string;
  title: string;
  composer: string | null;
}

/** Ce qu'on sait d'un récital sans être dans sa salle. */
export interface RecitalSummary {
  id: string;
  title: string;
  /** Qui joue. `null` tant que l'interprète n'a pas choisi de nom. */
  performer: { nickname: string | null };
  piece: RecitalPiece | null;
  /** ISO 8601, avec décalage. */
  scheduledAt: string;
  /**
   * Programmé pour tout de suite. La salle d'attente ne montre pas de compte
   * à rebours : lu à l'horloge du navigateur, il en afficherait quelques
   * secondes chez qui retarde sur le serveur.
   */
  immediate: boolean;
  /** `null` : places illimitées. Sinon 1..500. */
  seats: number | null;
  registrations: number;
  /** Au plus douze pseudonymes, dans l'ordre d'inscription. */
  attendees: readonly { nickname: string }[];
  /**
   * Dépendent de l'appelant, donc faux sans compte — y compris dans le HTML
   * servi, que le serveur Next lit sans jeton. La première sonde client les
   * corrige.
   */
  registered: boolean;
  mine: boolean;
  status: RecitalStatus;
  startedAt: string | null;
  endedAt: string | null;
  /**
   * L'interprète est sur scène, portes fermées : il branche sa caméra, il se
   * cadre. Le récital est encore « à venir » pour tout le monde, et rien ne
   * s'enregistre — c'est `openRecital` qui le fait commencer.
   */
  preparing: boolean;
  /**
   * La salle, en direct — et, pour son seul interprète, dès qu'il est monté sur
   * scène : c'est par là qu'il y remonte après avoir rechargé son onglet. Le
   * jeton reste la porte.
   */
  room: string | null;
  /** À l'écoute en ce moment ; 0 hors direct. */
  listeners: number;
  /**
   * Un récital privé n'est listé nulle part : on y entre par son lien — sa
   * page — ou par son code.
   */
  visibility: LiveVisibility;
  /**
   * Les six caractères d'un récital privé, rendus **à l'interprète seul**
   * (`mine`). `null` partout ailleurs, et toujours pour un public : c'est le
   * seul secret de ce type, il ne voyage dans aucune liste.
   */
  code: string | null;
  /** Ce que l'interprète a voulu. L'interprète choisit jusqu'au début. */
  recording: RecitalRecording;
  /**
   * L'enregistrement peut-il être proposé : faux sur un environnement sans
   * enregistreur, où la salle d'attente ne montre pas le choix.
   */
  recordable: boolean;
  /**
   * Sans public : le récital se joue pour être enregistré, et personne d'autre
   * n'entre. Privé, sans code ni places, toujours enregistré, en 720p.
   */
  recordingOnly: boolean;
  /**
   * Un replay est déposé, et ouvert à qui regarde : son interprète toujours,
   * les autres une fois publié. Faux tant que le récital n'est pas fini.
   */
  replay: boolean;
  /**
   * Et il est ouvert à tous : l'interprète l'a publié. Le lien du récital mène
   * alors au replay, sans compte.
   */
  replayPublished: boolean;
}

/** La liste publique : à venir et en cours, les plus proches d'abord. */
export interface RecitalListing {
  recitals: readonly RecitalSummary[];
  /**
   * La programmation peut-elle proposer l'enregistrement. Absent d'une API
   * d'avant l'enregistrement : il n'est pas proposé.
   */
  recordable?: boolean;
}

/**
 * « Mes récitals » : ceux que je donne, ceux où je suis inscrit·e.
 *
 * Une seule réponse pour les deux listes, parce que la page les montre
 * ensemble et que l'onglet ne fait que choisir laquelle passe en tête.
 */
export interface MyRecitals {
  performing: readonly RecitalSummary[];
  attending: readonly RecitalSummary[];
}

/** Ce qu'on donne pour programmer. */
export interface ScheduleRecitalOptions {
  title: string;
  /** ISO 8601. L'API exige un instant à venir, à moins d'un an. */
  scheduledAt: string;
  /** Absent : illimité. Sinon 1..500. */
  seats?: number;
  pieceId?: string;
  /** Vrai : visible seulement avec son lien ou son code. Absent : public. */
  private?: boolean;
  /** Vrai : le moment est celui du serveur, `scheduledAt` est ignoré. */
  startNow?: boolean;
  /** Vrai : l'interprète demande que son récital s'enregistre. Absent : non. */
  record?: boolean;
  /**
   * Vrai : sans public, pour l'enregistrer. L'API le rend privé et enregistré,
   * et le refuse sans `startNow` : personne n'attend sa date. Ni places, ni
   * `private`, ni `record` n'ont à partir avec.
   */
  recordingOnly?: boolean;
}

export function isRecitalId(value: string): boolean {
  return RECITAL_ID_PATTERN.test(value);
}

/**
 * Le code à montrer, ou `null`.
 *
 * À tous pour un récital public, à l'interprète seul pour un privé — l'API ne
 * rend pas autre chose, la règle est redite ici pour ne pas dépendre d'elle.
 * Et seulement tant qu'il peut encore mener quelque part : un code d'un
 * récital terminé ou annulé est rendu à la réserve côté API, et l'afficher en
 * grand promettrait une porte qui n'existe plus.
 */
export function recitalCodeToShow(recital: RecitalSummary): string | null {
  if (recital.code === null || (recital.visibility === "private" && !recital.mine)) {
    return null;
  }

  if (recital.status !== "scheduled" && recital.status !== "live") {
    return null;
  }

  return recital.code;
}

/**
 * Les récitals à venir et en cours.
 *
 * Route publique, jeton joint s'il existe : c'est lui qui permet à l'API de
 * marquer `registered` et `mine`, et sans lui on proposerait « Je serai là »
 * à quelqu'un qui a déjà sa place.
 */
export async function listRecitals(signal?: AbortSignal): Promise<RecitalListing> {
  return read<RecitalListing>("GET", RECITALS_PATH, { signal });
}

/** Un récital, ou une `ApiError` 404 quand il n'existe pas. */
export async function getRecital(
  id: string,
  signal?: AbortSignal,
): Promise<RecitalSummary> {
  return read<RecitalSummary>("GET", recitalPath(id), { signal });
}

/**
 * Le récital qui porte ce code, ou une `ApiError` avec la phrase à montrer.
 *
 * Route publique : un code se donne de vive voix, et la personne qui le
 * reçoit doit pouvoir voir chez qui il mène avant qu'on lui demande un compte.
 * Le jeton est joint s'il existe, pour que `registered` soit juste en arrivant
 * dans la salle.
 *
 * Le 404 n'est pas rattrapé : « aucun récital n'a ce code » est une réponse,
 * et c'est l'écran du code qui sait la mettre en rouge sous le champ. La
 * phrase est posée ici plutôt que lue dans l'API, parce que `describe` rend
 * pour tout 404 celle d'un récital introuvable par son identifiant.
 */
export async function lookupRecitalCode(code: string): Promise<RecitalSummary> {
  if (!isLiveCode(code)) {
    throw new ApiError("Un code fait six caractères.", 400);
  }

  try {
    return await read<RecitalSummary>(
      "GET",
      `${RECITALS_PATH}/by-code/${encodeURIComponent(code)}`,
    );
  } catch (cause) {
    if (cause instanceof ApiError && cause.status === 404) {
      throw new ApiError("Aucun récital n'a ce code.", 404);
    }

    throw cause;
  }
}

/** Ceux que je donne, ceux où je suis inscrit·e. Exige un compte. */
export async function myRecitals(signal?: AbortSignal): Promise<MyRecitals> {
  return read<MyRecitals>("GET", `${RECITALS_PATH}/mine`, { signal });
}

/** Programmer. Exige un compte ; l'API en fait l'interprète. */
export async function scheduleRecital(
  options: ScheduleRecitalOptions,
): Promise<RecitalSummary> {
  const body: Record<string, unknown> = {
    title: options.title,
    scheduledAt: options.scheduledAt,
  };

  if (options.seats !== undefined) {
    body.seats = options.seats;
  }

  if (options.pieceId !== undefined && options.pieceId !== "") {
    body.pieceId = options.pieceId;
  }

  // Envoyé seulement quand il dit quelque chose : public est le défaut de
  // l'API, et un `private: false` explicite ne lui apprendrait rien.
  if (options.private === true) {
    body.private = true;
  }

  if (options.startNow === true) {
    body.startNow = true;
  }

  // Comme `private` : rien ne s'enregistre par défaut, et seul un oui se dit.
  if (options.record === true) {
    body.record = true;
  }

  if (options.recordingOnly === true) {
    body.recordingOnly = true;
  }

  return read<RecitalSummary>("POST", RECITALS_PATH, { body });
}

/**
 * Réserver sa place. Idempotent : se réinscrire rend le même récital.
 *
 * Le 409 (complet, terminé, annulé) n'est pas rattrapé : c'est une réponse,
 * pas une panne, et la salle sait l'afficher sous le bouton avec les mots de
 * l'API.
 */
export async function registerToRecital(id: string): Promise<RecitalSummary> {
  return read<RecitalSummary>("POST", `${recitalPath(id)}/register`);
}

/** Rendre sa place. Idempotent, sans corps. */
export async function unregisterFromRecital(id: string): Promise<void> {
  await send("DELETE", `${recitalPath(id)}/register`);
}

/**
 * Monter sur scène : la salle s'ouvre, pour l'interprète seul.
 *
 * Le récital ne commence pas — c'est le sas, le temps de brancher un téléphone
 * et de se cadrer. Personne d'autre n'obtient de jeton, et rien ne
 * s'enregistre, jusqu'à `openRecital`.
 *
 * Idempotent : appelé une seconde fois (reconnexion), l'API rend l'état
 * courant, avec la même `room`. C'est elle qu'on pousse vers le studio.
 */
export async function startRecital(id: string): Promise<RecitalSummary> {
  return read<RecitalSummary>("POST", `${recitalPath(id)}/start`);
}

/**
 * Ouvrir au public : le récital commence.
 *
 * D'un coup — la porte s'ouvre aux spectateurs, le récital passe « en direct »
 * pour tout le monde, et l'enregistrement démarre. Se fait depuis le studio,
 * une fois cadré. Idempotent.
 */
export async function openRecital(id: string): Promise<RecitalOpening> {
  return read<RecitalOpening>("POST", `${recitalPath(id)}/open`);
}

/** Ce que l'ouverture rend : le récital est en direct, et voilà ce qu'il enregistre. */
export interface RecitalOpening {
  status: RecitalStatus;
  /** « unavailable » quand l'enregistreur n'a pas répondu : la marque ne ment pas. */
  recording: LiveRecitalRecording;
}

/**
 * Enregistrer ou non son récital. Interprète seulement, jusqu'au début : une
 * fois le récital commencé, il ne peut plus qu'arrêter
 * (`stopRecitalRecording`). Rend l'état de l'enregistrement.
 */
export async function chooseRecitalRecording(id: string, record: boolean): Promise<RecitalRecording> {
  const reply = await read<{ recording: RecitalRecording }>("POST", `${recitalPath(id)}/recording`, {
    body: { record },
  });

  return reply.recording;
}

/**
 * Arrêter l'enregistrement, pendant le récital. Interprète seulement. Rien ne
 * le relance ensuite. Idempotent. Rend l'état de l'enregistrement.
 */
export async function stopRecitalRecording(id: string): Promise<RecitalRecording> {
  const reply = await read<{ recording: RecitalRecording }>("DELETE", `${recitalPath(id)}/recording`);

  return reply.recording;
}

/**
 * Une prise : un essai, dans un enregistrement qui en porte plusieurs.
 *
 * Quelqu'un qui s'enregistre seul recommence, et chaque essai est une prise.
 * Le découpage vient de l'enregistreur, qui le décrit dans son manifeste —
 * rien n'est deviné ici, et rien n'est découpé dans les fichiers : une prise
 * est une **fenêtre** dans le même dépôt.
 *
 * Les instants sont en millisecondes Unix, comme les journaux du dépôt ; la
 * position est celle de la séance, et `null` dit « inconnue » plutôt que zéro.
 */
/**
 * Ce que la page d'une prise publiée sait de son récital : un extrait de sa
 * fiche, rendu par la route de la prise (`/api/live/takes/{id}`) — la fiche
 * entière d'un enregistrement sans public n'est pas à montrer.
 */
export type SharedRecital = Pick<
  RecitalSummary,
  "id" | "title" | "performer" | "piece" | "visibility" | "recordingOnly"
>;

export interface RecitalTake {
  /** Ce que porte son lien partagé, `/takes/{id}` : le rang ne sort pas. */
  id: string;
  /** Son nom propre, `null` sans : elle se partage alors sous celui de l'enregistrement. */
  title?: string | null;
  /** Le rang, à partir de 1 : « Prise 2 » se lit tel quel. */
  number: number;
  startedAtMs: number;
  endedAtMs: number;
  durationMs: number;
  startPositionMs: number | null;
  endPositionMs: number | null;
  /** Les notes jouées, quand l'enregistreur les a comptées. */
  notes: number | null;
  /** Celle-ci s'ouvre à qui a le lien du récital. */
  published: boolean;
}

/**
 * Ce que la salle d'attente lit d'un replay — et pas toute sa fiche.
 *
 * Le lecteur du studio a la sienne (`lib/live/replay/replay-loader.ts`), avec
 * les fichiers, le jeton signé et le reste : la salle d'attente n'a besoin que
 * de savoir s'il y a des prises et lesquelles. Deux vues d'une même réponse,
 * chacune du côté qui s'en sert.
 */
export interface RecitalReplay {
  id: string;
  durationMs: number;
  published: boolean;
  mine: boolean;
  /** Vide quand il n'y a rien à choisir : une seule traversée. */
  takes: readonly RecitalTake[];
}

/**
 * La fiche du replay d'un récital.
 *
 * Interdite à qui n'y a pas droit (403) et absente quand il n'y a rien à
 * revoir (404) : l'appelant regarde `replay` dans le résumé avant de la
 * demander, et traite le refus comme une absence.
 */
export async function getRecitalReplay(
  id: string,
  signal?: AbortSignal,
): Promise<RecitalReplay> {
  const reply = await read<Omit<RecitalReplay, "takes"> & { takes?: readonly RecitalTake[] }>(
    "GET",
    `${recitalPath(id)}/replay`,
    { signal },
  );

  // Une API d'avant les prises n'en rend aucune : la salle d'attente montre
  // alors ce qu'elle montrait, sans liste.
  return { ...reply, takes: reply.takes ?? [] };
}

/**
 * Publier le replay, ou le retirer. Interprète seulement.
 *
 * Un récital joué n'est pas un récital publié : le premier oui — enregistrer —
 * se donne avant de monter sur scène, celui-ci après avoir écouté. Publié, le
 * replay s'ouvre au lien du récital, sans compte.
 *
 * `take` vise **une prise** : on ne montre pas cinq essais quand on en garde
 * un. Sans lui, le geste est celui d'avant — le récital entier, donc chacune
 * de ses prises —, et `DELETE` sans rang retire tout.
 */
export async function publishRecitalReplay(
  id: string,
  published: boolean,
  take?: number,
): Promise<boolean> {
  const reply = await read<{ published: boolean }>(
    published ? "POST" : "DELETE",
    // Le rang voyage dans l'adresse : un `DELETE` sans corps est plus sûr à
    // travers les relais, et l'API le lit des deux côtés.
    take === undefined
      ? `${recitalPath(id)}/replay/publish`
      : `${recitalPath(id)}/replay/publish?take=${encodeURIComponent(take)}`,
  );

  return reply.published;
}

/**
 * Ce que la suppression d'une prise laisse derrière elle.
 *
 * `recording` est `null` quand c'était la dernière : le dépôt n'avait plus rien
 * à montrer, il est parti en entier, et l'écran n'a plus d'enregistrement à
 * ouvrir. `freedBytes` est la place rendue — supprimer une prise est un geste
 * qu'on fait pour ça, et le chiffre est la seule preuve que ça a servi.
 */
export interface TakeDeletion {
  recording: string | null;
  takes: readonly RecitalTake[];
  /** Quelque chose reste ouvert au lien du récital. */
  published: boolean;
  freedBytes: number;
}

/**
 * Supprime une prise : la ligne **et** les octets. Interprète seulement.
 *
 * Irréversible, comme la suppression d'un enregistrement — il n'y a pas de
 * corbeille. Emporte le MIDI de la prise, ses gestes dans le journal et ses
 * images dans les fichiers de caméra ; les prises gardées ne bougent pas d'une
 * milliseconde.
 *
 * Les octets partent avant la ligne : un refus (502) laisse donc la prise en
 * place, et le geste se rejoue. L'appelant repose la liste rendue plutôt que de
 * retirer la ligne de son côté.
 */
export async function deleteRecitalTake(id: string, take: number): Promise<TakeDeletion> {
  const reply = await read<Partial<TakeDeletion>>(
    "DELETE",
    `${recitalPath(id)}/replay/takes/${encodeURIComponent(take)}`,
  );

  return {
    recording: reply.recording ?? null,
    takes: reply.takes ?? [],
    published: reply.published ?? false,
    freedBytes: reply.freedBytes ?? 0,
  };
}

/**
 * Nomme une prise, ou lui rend son nom d'emprunt (une chaîne vide).
 * Interprète seulement.
 *
 * C'est le nom sous lequel elle se partage : sa page, son affiche, « Mes
 * publications ». Rend la liste des prises telle qu'elle est maintenant, que
 * l'appelant repose.
 */
export async function renameRecitalTake(id: string, take: number, title: string): Promise<readonly RecitalTake[]> {
  const reply = await read<{ takes?: readonly RecitalTake[] }>(
    "POST",
    `${recitalPath(id)}/replay/takes/${encodeURIComponent(take)}/title`,
    { body: { title } },
  );

  return reply.takes ?? [];
}

/* ── Mes publications ─────────────────────────────────────────────────────── */

/**
 * Une chose publiée : une prise, ou un enregistrement entier.
 *
 * L'unité est la **prise**, parce que c'est elle qui porte un lien. Un
 * enregistrement publié en entier n'en a qu'un — celui du récital — et fait
 * donc une seule ligne, sans rang (`take` vaut `null`).
 */
export interface RecitalPublication {
  recordingId: string;
  recital: { id: string; title: string; piece: RecitalPiece | null };
  /** Le nom de l'enregistrement, comme « Mes enregistrements » l'affiche. */
  title: string;
  /** Le rang de la prise, ou `null` : c'est l'enregistrement entier. */
  take: number | null;
  /** L'identifiant de la prise, pour son lien ; `null` avec `take`. */
  takeId: string | null;
  durationMs: number;
  notes: number | null;
  /** ISO 8601, ou `null` pour une publication d'avant que l'instant soit retenu. */
  publishedAt: string | null;
  startedAt: string;
}

/**
 * « Mes publications » : ce que le monde peut voir, du plus récent au plus
 * ancien. Compte obligatoire.
 *
 * Dépublier ne passe pas par ici mais par `publishRecitalReplay` : une seule
 * route sait ouvrir et refermer.
 */
export async function myPublications(
  signal?: AbortSignal,
): Promise<readonly RecitalPublication[]> {
  const reply = await read<{ publications?: readonly RecitalPublication[] }>(
    "GET",
    RECORDINGS_PATH + "/published",
    { signal },
  );

  return reply.publications ?? [];
}

/** Retirer le récital de l'affiche. Interprète seulement, avant démarrage. */
export async function cancelRecital(id: string): Promise<void> {
  await send("DELETE", recitalPath(id));
}

/** Un appairage de caméra : le code à mettre dans le lien, et son échéance. */
export interface CameraPairing {
  code: string;
  /** Échéance du code, en secondes Unix. */
  expiresAt: number;
}

/**
 * Prépare le lien qui fera filmer un téléphone. Interprète seulement, récital
 * en cours. Le code sert une fois, pendant dix minutes. Voir
 * `docs/spec/live-camera.md`.
 */
export async function createCameraPairing(id: string): Promise<CameraPairing> {
  return read<CameraPairing>("POST", `${recitalPath(id)}/camera-pairings`);
}

/**
 * Retire un lien de caméra avant qu'un téléphone ne l'ouvre : l'interprète l'a
 * annulé, ou en demande un autre. Sans effet sur un code déjà servi ou expiré.
 */
export async function revokeCameraPairing(id: string, code: string): Promise<void> {
  await send("POST", `${recitalPath(id)}/camera-pairings/revoke`, { body: { code } });
}

/* ── Mes enregistrements ──────────────────────────────────────────────────── */

/**
 * Un enregistrement déposé, tel que « Mes enregistrements » le liste.
 *
 * # Ce n'est pas un `RecitalSummary`
 *
 * Un résumé de récital répond à « où ai-je rendez-vous ? » : une date, des
 * places, des inscrits, une salle, un code d'entrée. Celui-ci répond à
 * « qu'est-ce que j'ai gardé ? ». Les deux se ressemblent de loin et n'ont
 * presque aucun champ en commun.
 *
 * # Deux titres, et il en faut deux
 *
 * `title` est le nom à afficher, déjà résolu par l'API : le sien, ou à défaut
 * celui du récital. `ownTitle` est le nom propre, `null` quand il n'en a pas —
 * c'est lui que le champ de renommage pré-remplit, sinon on y retrouverait le
 * titre du récital recopié, et valider le figerait sans l'avoir voulu.
 */
export interface RecordingSummary {
  id: string;
  /** Le nom à l'écran : le sien, sinon celui du récital. Jamais vide. */
  title: string;
  /** Le nom propre, `null` quand il emprunte celui du récital. */
  ownTitle: string | null;
  /** D'où il vient. Son identifiant ouvre le replay, les fichiers passent par lui. */
  recital: { id: string; title: string; piece: RecitalPiece | null };
  /** ISO 8601 : le début de l'enregistrement, pas celui du récital. */
  startedAt: string;
  durationMs: number;
  /** Les essais. Zéro pour un enregistrement d'une seule traversée. */
  takes: number;
  publishedTakes: number;
  /** Quelque chose est ouvert au lien du récital : tout, ou une prise. */
  published: boolean;
  /** Et c'est l'enregistrement entier. La nuance se dit à l'écran. */
  publishedWhole: boolean;
  /** Les pistes vidéo et audio déposées. */
  tracks: number;
  /** Images et son se sont arrêtés faute de place : le replay sera amputé. */
  degraded: boolean;
  /** Ni geste, ni piste : il n'y a rien à revoir, seulement de la place à rendre. */
  empty: boolean;
  /** Ce qu'il pèse contre le forfait, en octets ; `null` tant qu'il n'est pas mesuré. */
  bytes: number | null;
  /**
   * ISO 8601 : quand il sera effacé, parce qu'il dépasse ce que le forfait
   * garde — en place ou en âge. `null` : il est gardé.
   */
  deletionScheduledAt: string | null;
}

/**
 * « Mes enregistrements » : tout ce qu'on a gardé, du plus récent au plus
 * ancien.
 *
 * Par l'interprète du récital : un enregistrement n'a pas de propriétaire à
 * lui, c'est le récital qui dit à qui il est. Compte obligatoire, comme « mes
 * récitals ».
 */
export async function myRecordings(signal?: AbortSignal): Promise<readonly RecordingSummary[]> {
  const reply = await read<{ recordings?: readonly RecordingSummary[] }>(
    "GET",
    RECORDINGS_PATH + "/mine",
    { signal },
  );

  return reply.recordings ?? [];
}

/**
 * Renomme un enregistrement, ou lui rend le titre de son récital.
 *
 * Le récital garde le sien : c'est ce qui a été annoncé, ce que les inscrits
 * ont lu, et ce que porte son lien public. Une chaîne vide efface le nom propre
 * — c'est la seule façon de défaire un renommage, et elle se fait au même
 * endroit que lui.
 *
 * Rend la ligne telle que l'API l'a retenue, repli compris : l'écran la repose
 * sans recharger toute la liste, et ce qu'il affiche est ce qui est enregistré.
 */
export async function renameRecording(
  id: string,
  title: string,
): Promise<RecordingSummary> {
  return read<RecordingSummary>("POST", `${recordingPath(id)}/title`, {
    body: { title },
  });
}

/**
 * Dit ce que filmait une caméra d'un enregistrement : des mains, ou un visage.
 * Interprète seulement.
 *
 * C'est ce qui place l'image au replay — des mains au pied du rouleau, un
 * visage en bulle —, et la séance a pu se tromper : une webcam restée sur
 * « mains » qui filmait le visage. La correction passe devant ce que le
 * journal des états a gardé, sans le réécrire.
 *
 * `participant` est l'identité LiveKit de qui publiait la caméra, celle du
 * manifeste. Rend la table telle que l'API l'a retenue.
 */
export async function correctRecordingCamera(
  id: string,
  participant: string,
  subject: VideoSubject,
): Promise<Readonly<Record<string, VideoSubject>>> {
  const reply = await read<{ cameras?: unknown }>("POST", `${recordingPath(id)}/cameras`, {
    body: { participant, subject },
  });

  return readCorrectedSubjects(reply.cameras);
}

/**
 * Supprime un enregistrement : les fichiers du dépôt, puis la ligne.
 *
 * Irréversible — il n'y a pas de corbeille. Emporte les prises, qui sont des
 * fenêtres dans ces fichiers, et le replay publié s'il l'était : le lien du
 * récital redevient une page qui dit « terminé ». Le récital, lui, reste.
 *
 * Un échec laisse tout en place, y compris la ligne : c'est elle qui permet de
 * réessayer. L'appelant remet donc la ligne à l'écran plutôt que de la croire
 * partie.
 */
export async function deleteRecording(id: string): Promise<void> {
  await send("DELETE", recordingPath(id));
}

function recordingPath(id: string): string {
  // Même forme qu'un identifiant de récital, et même garde : une adresse
  // construite avec autre chose viserait une route qui n'existe pas, et le
  // refus arriverait du serveur sous une forme qu'on ne sait pas lire.
  if (!isRecitalId(id)) {
    throw new ApiError("Enregistrement inconnu.", 400);
  }

  return `${RECORDINGS_PATH}/${id}`;
}

/**
 * Le récital d'une prise publiée, depuis l'identifiant de son lien : ce que lit
 * le studio ouvert sur `/studio?take=<uuid>`, sans récital dans l'adresse.
 * 404 pour une prise qu'on ne montre pas.
 */
export async function getTakeRecital(takeId: string, signal?: AbortSignal): Promise<string> {
  if (!isRecitalId(takeId)) {
    throw new ApiError("Prise introuvable.", 404);
  }

  const found = await read<{ id: string; recital: { id: string } }>("GET", `/api/live/takes/${takeId}`, { signal });

  return found.recital.id;
}

function recitalPath(id: string): string {
  if (!isRecitalId(id)) {
    throw new ApiError("Récital inconnu.", 400);
  }

  return `${RECITALS_PATH}/${id}`;
}

async function read<T>(
  method: "GET" | "POST" | "DELETE",
  path: string,
  options: { body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const response = await send(method, path, options);

  return (await response.json()) as T;
}

/** La requête, avec le jeton s'il existe. Lève une `ApiError` sur tout refus. */
async function send(
  method: "GET" | "POST" | "DELETE",
  path: string,
  options: { body?: unknown; signal?: AbortSignal } = {},
): Promise<Response> {
  const headers = authHeaders();

  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  if (!response.ok) {
    throw new ApiError(await describe(response), response.status);
  }

  return response;
}

/**
 * Le jeton de compte est joint quand il existe, sans être exigé : les
 * lectures sont ouvertes aux visiteurs, « mes récitals » excepté, et sur les routes qui l'exigent c'est
 * l'API qui répond 401 — pas à ce module de deviner l'état de la session.
 */
function authHeaders(): Record<string, string> {
  const token = getToken();

  if (!token) {
    return {};
  }

  return { Authorization: `Bearer ${token}` };
}

/**
 * Le message à montrer. Celui de l'API quand elle en écrit un (`{ error }`),
 * sinon une phrase à nous : le 401 du pare-feu, par exemple, arrive en
 * anglais et n'a rien à faire sous un bouton.
 */
async function describe(response: Response): Promise<string> {
  if (response.status === 503) {
    return "Le live n'est pas disponible sur cet environnement.";
  }

  if (response.status === 429) {
    return "Trop de demandes coup sur coup. Réessaie dans un instant.";
  }

  if (response.status === 401) {
    return "Il faut un compte pour ça.";
  }

  if (response.status === 404) {
    return "Ce récital n'existe pas, ou plus.";
  }

  try {
    const body = (await response.json()) as { error?: string };

    if (typeof body.error === "string" && body.error !== "") {
      return body.error;
    }
  } catch {
    // Corps illisible : la phrase générique ci-dessous fera l'affaire.
  }

  return "Le récital n'a pas répondu. Réessaie dans un instant.";
}
